import { useMemo, type ReactNode } from 'react';
import { CssBaseline } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';
//
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverrides from './overrides';
import shadows, { customShadows } from './shadows';

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
  /**
   * The original app shipped a settings drawer but had it commented out and
   * pinned to light mode, so light is the default here too.
   */
  mode?: 'light' | 'dark';
};

export default function ThemeProvider({ children, mode = 'light' }: Props) {
  const isLight = mode === 'light';

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      palette: isLight ? palette.light : palette.dark,
      typography,
      breakpoints,
      shape: { borderRadius: 4 },
      direction: 'ltr',
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark,
    }),
    [isLight]
  );

  const theme = useMemo(() => {
    const base = createTheme(themeOptions);
    // The overrides need the finished theme to read palette/shadow values,
    // so they are attached after createTheme rather than passed into it.
    base.components = componentsOverrides(base) as Theme['components'];
    return base;
  }, [themeOptions]);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
}
